import type { RxDocument } from 'rxdb';
import type { ChatAddToolOutputFunction, ChatRequestOptions, UIMessage } from 'ai';
import { getAiToolkit } from '@tiptap-pro/ai-toolkit';

import {
  buildAgentDocumentContext,
  buildAgentInstructionBodyFields,
  serializeUserSources,
  type AgentInstructionBodyFields,
} from '@/hooks/llm/prompt-invocation-builders';
import { NAME_ALREADY_UP_TO_DATE_REASON, type SetDocumentNameOutput } from '@flintmint/core-shared/utils/editor-write-tool';
import { executeTiptapTool } from '@flintmint/web-shared/utils/llm-agent-utils';
import { TIPTAP_TOOL_NAMES } from '@flintmint/web-shared/utils/llm-message-utils';
import {
  getAgentRunEditTarget,
  getAgentRunEditScope,
  getCurrentAgentRunId,
  getToolExecutionIgnoreReason,
  publishAgentRunEvent,
} from '../run/agent-run-store';
import { getTargetedEditRange } from '../edit-target';
import { executeReplaceSelectionTool } from '../streaming/replace-selection-tool';
import { getRequestEditor } from '../request-editor';
import { isBlockDocument, isNoteDocument, type PersistedDocument } from '../document-utils';
import type { Block, Note, UserSource } from '@flintmint/core-shared/rxdb';

type AddToolOutput = ChatAddToolOutputFunction<UIMessage>;
type AgentToolCallRuntime = {
  addToolOutput: AddToolOutput;
  document?: PersistedDocument;
  getContext: (params: any) => Promise<any>;
};

export const getContextParams = (document: PersistedDocument) =>
  isNoteDocument(document) ? { noteDocument: document as RxDocument<Note> } : { blockDocument: document as RxDocument<Block> };

export function buildChatRequestOptions({
  userSources = [],
  documentInstructions,
  blocksInstructions,
}: { userSources?: UserSource[] } & AgentInstructionBodyFields): ChatRequestOptions | undefined {
  const body = {
    ...(userSources.length && { userSources }),
    ...(documentInstructions?.length && { documentInstructions }),
    ...(blocksInstructions?.length && { blocksInstructions }),
  };

  return Object.keys(body).length ? { body } : undefined;
}

async function buildToolCallRequestOptions(runtime: AgentToolCallRuntime): Promise<ChatRequestOptions | undefined> {
  const document = runtime.document;
  if (!document) {
    return undefined;
  }

  try {
    const llmContext = await runtime.getContext(getContextParams(document));
    return buildChatRequestOptions({
      userSources: serializeUserSources(llmContext.userSources),
      ...buildAgentInstructionBodyFields(llmContext),
    });
  } catch {
    // A context-refresh failure here must not abort the tool's continuation; send without instructions.
    return undefined;
  }
}

async function handleGetDocumentContext(
  requestId: string,
  runtime: AgentToolCallRuntime,
  toolCallId: string,
  addToolOutput: AddToolOutput
) {
  try {
    const document = runtime.document;

    if (!document) {
      addToolOutput({ tool: 'getDocumentContext', toolCallId, output: { error: 'No document available' } });
      return;
    }

    const llmContext = await runtime.getContext(getContextParams(document));
    const requestOptions = buildChatRequestOptions({
      userSources: serializeUserSources(llmContext.userSources),
      ...buildAgentInstructionBodyFields(llmContext),
    });
    const editor = getRequestEditor(requestId);
    const schemaAwareness = editor ? getAiToolkit(editor).getHtmlSchemaAwareness() : undefined;
    const toolOutput: Parameters<AddToolOutput>[0] = {
      tool: 'getDocumentContext',
      toolCallId,
      output: buildAgentDocumentContext(llmContext, schemaAwareness, getAgentRunEditScope(requestId)),
    };
    if (requestOptions) {
      toolOutput.options = requestOptions;
    }

    addToolOutput(toolOutput);
  } catch (error) {
    addToolOutput({
      tool: 'getDocumentContext',
      toolCallId,
      output: { error: error instanceof Error ? error.message : 'Failed to get document context' },
    });
  }
}

async function handleSetDocumentName(
  runtime: AgentToolCallRuntime,
  input: { name: string },
  toolCallId: string,
  addToolOutput: AddToolOutput
) {
  const doc = runtime.document;
  const { name } = input;
  const respond = (output: SetDocumentNameOutput) => addToolOutput({ tool: 'setDocumentName', toolCallId, output });

  if (!doc || !(isNoteDocument(doc) || isBlockDocument(doc))) {
    respond({ applied: false, reason: 'Document cannot be renamed' });
    return;
  }

  if (doc.name === name) {
    respond({ applied: false, reason: NAME_ALREADY_UP_TO_DATE_REASON, name });
    return;
  }

  try {
    await doc.incrementalPatch({ name });
    respond({ applied: true, name });
  } catch (error) {
    respond({ applied: false, reason: error instanceof Error ? error.message : 'Failed to update name' });
  }
}

async function handleCachedEditorTool(
  requestId: string,
  toolName: string,
  toolCallId: string,
  addToolOutput: AddToolOutput,
  stop: () => void,
  execute: () => { status: 'missing-editor' } | { status: 'executed'; output: unknown }
) {
  const ignoreReason = getToolExecutionIgnoreReason(requestId, toolName);
  if (ignoreReason) {
    addToolOutput({ tool: toolName, toolCallId, output: { ignored: true, reason: ignoreReason } });
    return;
  }

  const runId = getCurrentAgentRunId(requestId);
  const executionResult = execute();

  if (executionResult.status === 'missing-editor') {
    if (runId) {
      publishAgentRunEvent(requestId, runId, { type: 'errored', network: false });
    }

    addToolOutput({ tool: toolName, toolCallId, output: { error: 'Editor is no longer available' } });
    stop();
    return;
  }

  addToolOutput({ tool: toolName, toolCallId, output: executionResult.output });
}

function executeCachedReplaceSelectionTool({
  requestId,
  toolCallId,
  input,
}: {
  requestId: string;
  toolCallId: string;
  input: unknown;
}): { status: 'missing-editor' } | { status: 'executed'; output: unknown } {
  const editor = getRequestEditor(requestId);
  if (!editor) {
    return { status: 'missing-editor' };
  }

  const result = executeReplaceSelectionTool({
    requestId,
    toolCallId,
    editor,
    input,
    fallbackRange: getTargetedEditRange(getAgentRunEditTarget(requestId)),
  });

  return { status: 'executed', output: result.output };
}

async function handleReplaceSelectionTool(
  requestId: string,
  input: unknown,
  toolCallId: string,
  addToolOutput: AddToolOutput,
  stop: () => void
) {
  return handleCachedEditorTool(requestId, 'replaceSelection', toolCallId, addToolOutput, stop, () =>
    executeCachedReplaceSelectionTool({
      requestId,
      toolCallId,
      input,
    })
  );
}

function executeCachedTiptapTool({
  requestId,
  toolCallId,
  toolName,
  input,
}: {
  requestId: string;
  toolCallId: string;
  toolName: string;
  input: unknown;
}): { status: 'missing-editor' } | { status: 'executed'; output: unknown } {
  // The backend omits tiptapEdit from the tool set on targeted runs (agent-mode.ts), so the model can
  // never call it here; this is the client-side safety net so a targeted run never writes outside the
  // selection (medical notes). Targeted writes must go through replaceSelection.
  if (toolName === 'tiptapEdit' && getTargetedEditRange(getAgentRunEditTarget(requestId)) != null) {
    return {
      status: 'executed',
      output: { success: false, applied: false, reason: 'Targeted writes must use replaceSelection instead of tiptapEdit.' },
    };
  }

  const editor = getRequestEditor(requestId);
  if (!editor) {
    return { status: 'missing-editor' };
  }

  const { output } = executeTiptapTool({
    editor,
    toolCallId,
    toolName,
    input,
  });

  return { status: 'executed', output };
}

async function handleTiptapTool(
  requestId: string,
  toolName: string,
  input: unknown,
  toolCallId: string,
  addToolOutput: AddToolOutput,
  stop: () => void
) {
  return handleCachedEditorTool(requestId, toolName, toolCallId, addToolOutput, stop, () =>
    executeCachedTiptapTool({
      requestId,
      toolCallId,
      toolName,
      input,
    })
  );
}

export async function handleAgentToolCall({
  requestId,
  runtime,
  toolCall,
  stop,
}: {
  requestId: string;
  runtime: AgentToolCallRuntime;
  toolCall: { toolName: string; input: unknown; toolCallId: string };
  stop: () => void;
}): Promise<void> {
  const { toolName, input, toolCallId } = toolCall;
  const { addToolOutput } = runtime;

  if (toolName === 'getDocumentContext') {
    return handleGetDocumentContext(requestId, runtime, toolCallId, addToolOutput);
  }

  if (toolName !== 'setDocumentName' && toolName !== 'replaceSelection' && !TIPTAP_TOOL_NAMES.has(toolName)) {
    return;
  }

  const requestOptions = await buildToolCallRequestOptions(runtime);
  // The AI SDK auto-continues after these tool outputs, so carry the refreshed user
  // instructions/sources on the continuation just like getDocumentContext does.
  const decoratedAddToolOutput: AddToolOutput = (output) =>
    addToolOutput(requestOptions ? { ...output, options: requestOptions } : output);

  if (toolName === 'setDocumentName') {
    return handleSetDocumentName(runtime, input as { name: string }, toolCallId, decoratedAddToolOutput);
  }

  if (toolName === 'replaceSelection') {
    return handleReplaceSelectionTool(requestId, input, toolCallId, decoratedAddToolOutput, stop);
  }

  return handleTiptapTool(requestId, toolName, input, toolCallId, decoratedAddToolOutput, stop);
}
